/**
 * DEL-1125: publish this build's identity into the served bundle.
 *
 * `scripts/verify-deployed-sdk.js` is the last gate before the
 * `delphina-prod-*` tag is written. Until this module existed it asserted only
 * that four identifier names from posthog.js appear in *some* served chunk —
 * markers that landed in `db28189cc`/`ce593761e`/`c6e1e8464`, weeks earlier. A
 * bundle from any commit since then passed, so the step named "Verify the live
 * bundle came from this commit" could not detect drift at all.
 *
 * The obvious fix is the Next.js `buildId`, and DEL-1125's brief proposed it.
 * That does not work here: this app is Next 16 App Router, which serves no
 * `__NEXT_DATA__` and no buildId anywhere — not in the HTML, not in the flight
 * payload, not in any of the 29 chunks (checked against live production). So
 * the build identifier has to be ours and it has to be embedded deliberately.
 *
 * ## This module is a diagnostic, not the gate's source (DEL-1776)
 *
 * DEL-1125 had the deploy gate recover the stamp by regex out of a minified
 * chunk, on the reasoning recorded below — that property names and string
 * literals are not mangled, so the assignment is greppable as written. That
 * reasoning is FALSE for this build pipeline, and the gate was broken against
 * real output as a result. A real Next 16.3.5 / Turbopack build emits:
 *
 *   206478,e=>{"use strict";
 *     let t=e.i(247167).default.env.NEXT_PUBLIC_DEPLOY_STAMP||"unstamped";
 *     "u">typeof globalThis&&(globalThis.__DELPHINA_DEPLOY_STAMP=t); ...
 *
 * The property name survives, as expected. The VALUE does not: Next no longer
 * inlines `NEXT_PUBLIC_*` into the bundle, it emits a runtime read of a
 * generated env module, and that literal lives in module 247167 — not in this
 * chunk. No amount of regex over this chunk can recover it.
 *
 * The module is kept because it is genuinely useful: the global is how a
 * running page could learn its build identity, and its presence in a served
 * chunk is a real "did the stamp module ship?" signal that the gate reports.
 * It is NOT how the gate decides anything.
 *
 * The gate's source is `public/deploy-stamp.json`, written verbatim by
 * `client/scripts/generate-deploy-stamp.cjs` in `prebuild` from the same
 * `NEXT_PUBLIC_DEPLOY_STAMP` variable. Nothing parses it, so no compiler,
 * minifier or bundler upgrade can change the answer. If you are here to
 * understand how the deploy gate proves which commit is live, read that script
 * — not this one.
 *
 * ## Properties that still hold (property 2 below, and why)
 *
 *  1. It is a side-effect-only module, imported with a bare `import './…'`.
 *     A module whose exports nobody reads can be dropped as unused; a module
 *     that writes to `globalThis` cannot.
 *  2. The global is assigned through a literal property name. JavaScript
 *     minifiers never rename property names, so `__DELPHINA_DEPLOY_STAMP`
 *     stays greppable in the minified output. This is what makes the
 *     presence diagnostic work. It is NOT sufficient to recover the value.
 *
 * production-deployment.yml sets NEXT_PUBLIC_DEPLOY_STAMP for the build step
 * from `${{ github.sha }}` and `${{ github.run_id }}`; a build made without it
 * stamps `unstamped`, which the deploy gate treats as "this build cannot prove
 * its own identity" rather than as a pass.
 */
export const DEPLOY_STAMP = process.env.NEXT_PUBLIC_DEPLOY_STAMP || 'unstamped';

/**
 * Global the stamp is published on. Must match DEPLOY_STAMP_GLOBAL in the
 * verify script.
 *
 * Note it is NOT used in the assignment below. An earlier version wrote
 * `globalThis[DEPLOY_STAMP_GLOBAL] = DEPLOY_STAMP`, and a real Turbopack build
 * emitted `const DEPLOY_STAMP_GLOBAL = '__DELPHINA_DEPLOY_STAMP'` followed by a
 * computed `globalThis[DEPLOY_STAMP_GLOBAL]` write — a shape a minifier is free
 * to reduce to `globalThis[t] = ...` with `t` renamed, taking the only
 * greppable copy of the name with it. JavaScript minifiers never rename property
 * names, so the assignment spells the property as a literal. Do not refactor
 * this into a constant.
 */
export const DEPLOY_STAMP_GLOBAL = '__DELPHINA_DEPLOY_STAMP';

if (typeof globalThis !== 'undefined') {
  globalThis.__DELPHINA_DEPLOY_STAMP = DEPLOY_STAMP;
}
